/**
 * ScadaViewer.jsx
 * Visor SCADA en tiempo real — canvas runtime puro, sin herramientas de edición.
 * Se abre en nueva pestaña via window.open('/#viewer', '_blank').
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Stage, Layer } from 'react-konva';
import { useScadaStore } from '../../store/scadaStore.js';
import { WslRuntime } from '../../engine/WslRuntime.js';
import { SerialBus } from '../../engine/SerialBus.js';
import { CanvasObject } from '../ScadaEditor/CanvasObjects.jsx';

// ─── Hook de runtime ──────────────────────────────────────────────────────────

function useViewerRuntime(hardware, screens, script) {
  const [tagValues,   setTagValues]   = useState({});
  const [runtimeMode, setRuntimeMode] = useState('—');
  const [running,     setRunning]     = useState(false);
  const [logs,        setLogs]        = useState([]);
  const runtimeRef = useRef(null);

  const addLog = useCallback((msg) => {
    setLogs(prev => [...prev.slice(-199), msg]);
  }, []);

  const start = useCallback(() => {
    if (runtimeRef.current) {
      runtimeRef.current.stop().catch(() => {});
      runtimeRef.current = null;
    }
    if (!hardware) { addLog('⚠ Sin hardware configurado'); return; }

    let rt;
    try {
      rt = new WslRuntime({
        hardware,
        script: script?.trim() || '',
        design: { screens },
        transportMode: hardware?.device?.mode || 'SIMULATION',
      });
    } catch (err) {
      addLog(`❌ Error al crear runtime: ${err.message}`);
      return;
    }

    rt.onTagChange  = (tag, value) => setTagValues(prev => ({ ...prev, [tag]: value }));
    rt.onLog        = ({ msg })    => addLog(msg);
    rt.onAlarm      = ({ message, level }) => addLog(`[${level}] ${message}`);
    rt.onError      = (err)        => addLog(`❌ ${err.message}`);

    runtimeRef.current = rt;

    rt.start()
      .then(() => {
        const m = rt.getMode?.() || hardware?.device?.mode || 'SIMULATION';
        setRuntimeMode(m);
        setRunning(true);
        addLog(`▶ Runtime iniciado — modo ${m}`);
      })
      .catch(err => {
        addLog(`❌ No se pudo iniciar: ${err.message}`);
        setRunning(false);
      });
  }, [hardware, screens, script, addLog]);

  const stop = useCallback(() => {
    runtimeRef.current?.stop().catch(() => {});
    runtimeRef.current = null;
    setRunning(false);
    setRuntimeMode('—');
    setTagValues({});
    addLog('⏹ Runtime detenido');
  }, [addLog]);

  const emitClick = useCallback((objId) => {
    runtimeRef.current?.emitClick(objId);
  }, []);

  // Arrancar al montar, detener al desmontar
  useEffect(() => {
    start();
    return () => { runtimeRef.current?.stop().catch(() => {}); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // solo al montar — recargas manuales via botón

  // Reiniciar si el proyecto cambia desde otra pestaña (localStorage event)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'wsl-scada-storage-v2') {
        addLog('🔄 Proyecto actualizado desde el editor — recargando...');
        setTimeout(() => window.location.reload(), 800);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [addLog]);

  return { tagValues, runtimeMode, running, logs, start, stop, emitClick };
}

// ─── Visor principal ─────────────────────────────────────────────────────────

export default function ScadaViewer() {
  const { currentProject } = useScadaStore();
  const hardware = currentProject?.hardware;
  const screens  = currentProject?.screens || [];
  const script   = currentProject?.script  || '';
  const name     = currentProject?.name    || hardware?.device?.name || 'SCADA Viewer';

  const [activeScreenId, setActiveScreenId] = useState(screens[0]?.id || '');
  const [showLog,        setShowLog]        = useState(false);
  const [serialConn,    setSerialConn]     = useState(SerialBus.isConnected?.() ?? false);
  const logEndRef = useRef(null);

  const activeScreen = screens.find(sc => sc.id === activeScreenId) || screens[0] || { objects: [], width: 1280, height: 720 };
  const canvasW = activeScreen.width  || 1280;
  const canvasH = activeScreen.height || 720;

  const { tagValues, runtimeMode, running, logs, start, stop, emitClick } = useViewerRuntime(hardware, screens, script);

  // Auto-scroll del log
  useEffect(() => {
    if (showLog) logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs, showLog]);

  // Badge serial
  useEffect(() => {
    const onConn    = () => setSerialConn(true);
    const onDisconn = () => setSerialConn(false);
    SerialBus.addEventListener?.('connected',    onConn);
    SerialBus.addEventListener?.('disconnected', onDisconn);
    return () => {
      SerialBus.removeEventListener?.('connected',    onConn);
      SerialBus.removeEventListener?.('disconnected', onDisconn);
    };
  }, []);

  const modeColor = { REMOTE: '#4d9fff', LOCAL: '#a78bfa', SIMULATION: '#f59e0b', AUTO: '#00d4aa' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#080d14', fontFamily: "'Segoe UI', sans-serif", color: '#e2e8f0', overflow: 'hidden' }}>

      {/* ── Barra superior ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', height: 48, background: '#0d1117', borderBottom: '1px solid #1a2035', flexShrink: 0 }}>

        {/* Nombre del proyecto */}
        <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1 }}>
          ⊡ {name}
        </span>

        {/* Tabs de pantallas */}
        <div style={{ display: 'flex', gap: 4, flex: 1, overflowX: 'auto' }}>
          {screens.map(sc => (
            <button
              key={sc.id}
              onClick={() => setActiveScreenId(sc.id)}
              style={{ padding: '4px 14px', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid', fontFamily: "'JetBrains Mono', monospace", background: sc.id === activeScreenId ? '#1e3a5f' : 'transparent', borderColor: sc.id === activeScreenId ? '#3b82f6' : '#1a2035', color: sc.id === activeScreenId ? '#60a5fa' : '#4a5568' }}
            >
              {sc.name}
            </button>
          ))}
        </div>

        {/* Badges de estado */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          {/* Modo de transporte */}
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: (modeColor[runtimeMode] || '#4a5568') + '22', border: `1px solid ${modeColor[runtimeMode] || '#4a5568'}`, color: modeColor[runtimeMode] || '#4a5568', fontFamily: "'JetBrains Mono', monospace" }}>
            {runtimeMode}
          </span>

          {/* Serial */}
          <span style={{ fontSize: 11, color: serialConn ? '#22c55e' : '#4a5568' }} title="Puerto serial">
            {serialConn ? '● USB' : '○ USB'}
          </span>

          {/* Tags activos */}
          <span style={{ fontSize: 10, color: '#4a5568', fontFamily: "'JetBrains Mono', monospace" }}>
            {Object.keys(tagValues).length} tags
          </span>

          {/* Botón log */}
          <button
            onClick={() => setShowLog(v => !v)}
            style={{ padding: '3px 10px', borderRadius: 4, fontSize: 10, cursor: 'pointer', background: showLog ? '#1e3a5f' : 'transparent', border: '1px solid #1a2035', color: '#4a5568', fontFamily: "'JetBrains Mono', monospace" }}
          >
            Log
          </button>

          {/* Start / Stop */}
          {running
            ? <button onClick={stop}  style={btnStyle('#ef4444')}>⏹ Detener</button>
            : <button onClick={start} style={btnStyle('#22c55e')}>▶ Iniciar</button>
          }
        </div>
      </div>

      {/* ── Canvas ─────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', background: '#080d14' }}>
        <Stage
          width={canvasW}
          height={canvasH}
          style={{ background: activeScreen.background || '#0f172a' }}
        >
          <Layer>
            {activeScreen.objects.map(obj => (
              <CanvasObject
                key={obj.id}
                obj={obj}
                isRuntime={true}
                tagValue={obj.tag ? tagValues[obj.tag] : undefined}
                onRuntimeClick={emitClick}
              />
            ))}
          </Layer>
        </Stage>
      </div>

      {/* ── Panel de log ───────────────────────────────────────────────────── */}
      {showLog && (
        <div style={{ height: 180, background: '#0d1117', borderTop: '1px solid #1a2035', overflowY: 'auto', padding: '8px 16px', flexShrink: 0 }}>
          {logs.length === 0
            ? <span style={{ color: '#4a5568', fontSize: 11 }}>Sin logs aún...</span>
            : logs.map((l, i) => (
                <div key={i} style={{ fontSize: 11, color: l.startsWith('❌') ? '#ef4444' : l.startsWith('⚠') ? '#f59e0b' : '#94a3b8', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.6 }}>{l}</div>
              ))
          }
          <div ref={logEndRef} />
        </div>
      )}
    </div>
  );
}

function btnStyle(color) {
  return { padding: '4px 12px', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: color + '22', border: `1.5px solid ${color}`, color, fontFamily: "'JetBrains Mono', monospace" };
}
